import { createApp } from 'vue'
import * as Sentry from '@sentry/vue'
import { pinia } from '@/modules/pinia'
import 'vuetify/dist/vuetify.min.css'

import { ComponentLibrary, useEventsBus } from '@haskoning/vue-component-library'
import {
	OpengroundPlugin,
	setSpeckleConfig,
	useSpeckleStore,
	VueSpecklePlugin
} from '@haskoning/vue-speckle'

import vuetify from './plugins/vuetify'
import router from './router'
import App from './App.vue'
import 'vuetify/dist/vuetify.min.css'
import HeaderImage from '/header.jpeg'
import HeaderImageLazy from '/header_lazy.jpg'

import { useApi } from '@/api/useApi'
import apiUrls from '@/api/apiUrls'
import { useAuthenticationStore } from '@/stores/authentication'
import { AuthenticationManagement } from '@haskoning/vue-authentication-middleware'
import { useSentryFeedback } from './composables/useSentryFeedback'
import { opengroundAdapter } from '@/adapters/openground'

const api = useApi()
const { bus } = useEventsBus()

pinia.use(({ store }) => {
	store.$api = api
	store.$apiUrls = apiUrls
})

setSpeckleConfig(
	import.meta.env.VITE_APP_SERVER_URL,
	import.meta.env.VITE_APP_SPECKLE_WORKSPACE_ID,
	import.meta.env.VITE_APP_SPECKLE_ID,
	import.meta.env.VITE_APP_SPECKLE_SECRET,
	import.meta.env.VITE_APP_SPECKLE_NAME
)

const app = createApp(App)
	.use(vuetify)
	.use(pinia)
	.use(OpengroundPlugin, {
		router,
		callbackURL: import.meta.env.VITE_APP_OPENGROUND_CALLBACK_URL,
		clientId: import.meta.env.VITE_APP_OPENGROUND_CLIENT_ID,
		backendAdapter: opengroundAdapter
	})
	.use(router)
	.use(VueSpecklePlugin, {
		componentsAutoSelectData: true,
		speckleStore: () => useSpeckleStore({ autoSelectData: false })
	})

const authStore = useAuthenticationStore(pinia)
app
	.use(AuthenticationManagement, {
		appTitle: 'Liquefaction',
		headerImage: HeaderImage,
		headerImageLazy: HeaderImageLazy,
		authStore: authStore,
		apiBaseUrl: import.meta.env.VITE_APP_API_BASE_URL.replace(/\/$/, ''),
		useLegacyAuthentication: true
	})
	.use(ComponentLibrary, { externalBus: bus })

Sentry.init({
  app,
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracePropagationTargets: ['*'],
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      triggerLabel: 'Feedback',
      formTitle: 'Send Feedback',
      submitButtonLabel: 'Send Feedback',
      cancelButtonLabel: 'Cancel',
      messagePlaceholder: 'What would you like to tell us?',
      successMessageText: 'Thank you for your feedback!',
      colorScheme: 'light',
      showName: true,
      showEmail: true,
      enableScreenshot: true,
      autoInject: false
    })
  ],
  // Performance Monitoring
  tracesSampleRate: 0.1, // Capture 10% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in cloud and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

app.mount('#app')

router.isReady().then(() => {
  useSentryFeedback()
})

export default app